import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
} from '@mui/material';
import { voidInvoice } from '../../services/esetApi';
import type { EsetVoidInvoiceResult } from '../../services/esetApi';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

/** Minimal identity the dialog needs; invoice details come from the endpoint on open. */
export interface EsetVoidInvoiceTarget {
  id: string;
  customerName: string | null;
  productBundle: string | null;
}

interface EsetVoidInvoiceDialogProps {
  sub: EsetVoidInvoiceTarget | null; // null = closed
  onClose: () => void;
  /** Called after a REAL run voids the invoice, so the table can refresh. */
  onVoided?: () => void;
}

/** Void (undo) an erroneously-raised renewal invoice: on open it dry-runs to fetch the bound
 *  invoice, then a single click deletes it in RepairShopr/Xero, drops the SM4RT mirror, unbinds
 *  the subscription, and clears the billing guards so it can be re-raised. */
export const EsetVoidInvoiceDialog: React.FC<EsetVoidInvoiceDialogProps> = ({ sub, onClose, onVoided }) => {
  const [loading, setLoading] = useState(false); // initial dry-run fetch
  const [running, setRunning] = useState(false); // the real void
  const [preview, setPreview] = useState<EsetVoidInvoiceResult | null>(null);
  const [result, setResult] = useState<EsetVoidInvoiceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // On open, dry-run to discover the bound invoice (number + paid state).
  useEffect(() => {
    if (!sub) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setResult(null);
    setError(null);
    voidInvoice(sub.id, { dry_run: true })
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the invoice');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sub]);

  if (!sub) return null;

  const close = () => onClose();

  const execute = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await voidInvoice(sub.id, { dry_run: false });
      setResult(res);
      if (res.status === 'voided') onVoided?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Void invoice failed');
    } finally {
      setRunning(false);
    }
  };

  const number = preview?.invoice_number ?? result?.invoice_number;
  const rsId = preview?.invoice_rs_id ?? result?.invoice_rs_id;
  const invoiceLabel = number ? `invoice ${number}` : 'the bound invoice';
  // What state are we in: nothing to void / paid (blocked) / voidable / done.
  const noInvoice = preview?.status === 'no_invoice';
  const paid = preview?.status === 'paid';
  const done = result?.status === 'voided';
  const canVoid = !loading && !running && !done && !!preview && !noInvoice && !paid;

  const busy = loading || running;

  return (
    <Dialog open onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Void invoice
        <Box sx={{ fontSize: 13, fontWeight: 400, color: '#888', mt: 0.25 }}>
          {sub.customerName || '—'} · {sub.productBundle || '—'}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : noInvoice ? (
          <Box sx={{ fontSize: 13, color: '#888', py: 3, textAlign: 'center' }}>
            No invoice is bound to this subscription — nothing to void.
          </Box>
        ) : paid ? (
          <Alert severity="warning">
            {invoiceLabel[0].toUpperCase() + invoiceLabel.slice(1)} is <strong>paid</strong> — it can't be
            deleted here. Reverse it with a credit note / refund in{' '}
            {rsId != null ? (
              <Link href={`${REPAIRSHOPR_BASE_URL}/invoices/${rsId}`} target="_blank" rel="noopener noreferrer">
                RepairShopr
              </Link>
            ) : (
              'RepairShopr'
            )}{' '}
            instead.
          </Alert>
        ) : (
          <>
            <Box sx={{ fontSize: 13, color: '#555', mb: 1 }}>
              This permanently deletes {invoiceLabel} in RepairShopr (which <strong>voids it in
              Xero</strong>), removes it from SM4RT, and resets the subscription's billing so the
              renewal can be raised again. This can't be undone.
            </Box>
            {rsId != null && (
              <Box sx={{ fontSize: 12, mb: 0.5 }}>
                <Link href={`${REPAIRSHOPR_BASE_URL}/invoices/${rsId}`} target="_blank" rel="noopener noreferrer">
                  View in RepairShopr
                </Link>{' '}
                first to confirm it's the right one.
              </Box>
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {result && (
          <Alert
            severity={result.status === 'voided' ? 'success' : 'warning'}
            sx={{ mt: 1.5 }}
          >
            {result.status === 'voided'
              ? `Done — ${invoiceLabel} was voided and removed.`
              : `No action: ${result.status}.`}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy} sx={{ color: '#aaa' }}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        {canVoid && (
          <Button variant="contained" color="error" onClick={execute} disabled={running}>
            {running ? 'Voiding…' : `Void ${invoiceLabel}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
